import {
  useDeletePaymentChannel,
  useGetPaymentChannel,
  useUpdatePaymentChannelStatus,
} from "@features/cx/settings/hooks";
import { toast } from "@shared/hooks/use-toast";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { Badge } from "@shared/ui/badge";
import { Button } from "@shared/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@shared/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@shared/ui/alert-dialog";
import { Switch } from "@shared/ui/switch";
import {
  Building,
  CreditCard,
  Edit,
  Globe,
  Plus,
  Smartphone,
  Trash2,
} from "lucide-react";
import { useState } from "react";
import { PaymentChannelForm } from "./paymentChannelForm/paymentChannelForm";

interface PaymentChannel {
  code: string;
  name: string;
  type: string;
  isActive: boolean;
  processingFee?: number;
  description?: string;
}

const channelIcons = {
  physical: Building,
  online: Globe,
  mobile: Smartphone,
  bank: CreditCard,
};

// Codes for channels that cannot be edited or deleted
const NON_EDITABLE_CODES = [
  "Mobile App#33",
  "Online Portal#5",
  "Walk-in Counter#4",
];

export function PaymentChannelsSettings() {
  const remote_utility_id = useRemoteUtilityId();
  const { data: channelsData } = useGetPaymentChannel();
  const updatePaymentChannelStatus = useUpdatePaymentChannelStatus();
  const deletePaymentChannel = useDeletePaymentChannel();
  const [channels, setChannels] = useState<PaymentChannel[]>(channelsData);
  const [showDialog, setShowDialog] = useState(false);
  const [editingChannel, setEditingChannel] = useState<PaymentChannel | null>(
    null
  );
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [channelToDelete, setChannelToDelete] = useState<PaymentChannel | null>(
    null
  );

  const handleAddChannel = () => {
    setEditingChannel(null);
    setShowDialog(true);
  };

  const handleEditChannel = (channel: PaymentChannel) => {
    setEditingChannel(channel);
    setShowDialog(true);
  };

  const handleDeleteChannel = (channel: PaymentChannel) => {
    setChannelToDelete(channel);
    setShowDeleteDialog(true);
  };

  const confirmDelete = () => {
    if (channelToDelete) {
      const payloadDelete = {
        code: channelToDelete.code,
        remote_utility_id: Number(remote_utility_id),
      };

      deletePaymentChannel.mutateAsync(payloadDelete);

      console.log("Deleting payment channel:", payloadDelete);

      setShowDeleteDialog(false);
      setChannelToDelete(null);

      toast({
        title: "Payment Channel Deleted",
        description: `${channelToDelete.name} has been deleted successfully.`,
      });
    }
  };

  const handleSaveChannel = (
    channelData: Omit<PaymentChannel, "id" | "isActive">
  ) => {
    if (editingChannel) {
      // Update existing channel
      setChannels((prev) =>
        prev.map((channel) =>
          channel.code === editingChannel.code
            ? { ...channel, ...channelData }
            : channel
        )
      );
    } else {
      // Add new channel
      const newChannel: PaymentChannel = {
        code: Date.now().toString(),
        ...channelData,
        isActive: true,
      };
      setChannels((prev) => [...prev, newChannel]);
    }
  };

  const handleToggleChannel = (code: string, checked: boolean) => {
    const channel = channelsData?.find((channel) => channel.code === code);
    if (!channel) {
      toast({
        title: "Error",
        description: "Channel not found",
        variant: "destructive",
      });
      return;
    }
    const payload = {
      code: channel.code,
      is_active: checked,
      remote_utility_id: Number(remote_utility_id),
    };
    updatePaymentChannelStatus.mutateAsync(payload);
  };

  const handleCloseDialog = () => {
    setShowDialog(false);
    setEditingChannel(null);
  };

  // Check if a channel is editable/deletable
  const isChannelEditable = (code: string) => {
    return !NON_EDITABLE_CODES.includes(code);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <p className="card-heading-text"> Payment Channels Configuration</p>
          <p className="card-subheading-text">
            Configure available payment channels and their processing fees
          </p>
        </div>
        <Button onClick={handleAddChannel}>
          <Plus className="mr-2 h-4 w-4" />
          Add Payment Channel
        </Button>
      </div>
      <div className="space-y-4 mt-5">
        {channelsData?.map((channel) => {
          const IconComponent =
            channelIcons[channel.type as keyof typeof channelIcons] ||
            CreditCard;
          const canEdit = isChannelEditable(channel.code);

          return (
            <div
              key={channel.code}
              className="flex items-center justify-between p-4 border rounded-lg"
            >
              <div className="flex items-center gap-4">
                <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <IconComponent className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3>{channel.name}</h3>
                    <Badge
                      variant={channel.isActive ? "secondary" : "outline"}
                      className={
                        channel.isActive ? "bg-green-100 text-green-800" : ""
                      }
                    >
                      {channel.isActive ? "Active" : "Inactive"}
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {channel.remark}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Processing Fee: {channel.processingFee}%
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={channel.isActive}
                  onCheckedChange={(checked) =>
                    handleToggleChannel(channel.code, checked)
                  }
                />
                <Button
                  variant="outline"
                  size="sm"
                  // disabled={channel.code === "" || !canEdit}
                  onClick={() => handleEditChannel(channel)}
                >
                  <Edit className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={channel.code === "" || !canEdit}
                  onClick={() => handleDeleteChannel(channel)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      <PaymentChannelForm
        isOpen={showDialog}
        onClose={handleCloseDialog}
        onSave={handleSaveChannel}
        editingChannel={editingChannel}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Payment Channel</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{channelToDelete?.name}"? This
              action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
